/**
 * @file        src/pages/wallet/SettleDueModal.jsx
 * @description Record an offline payment against a wallet's outstanding due, or
 *              write it off. Password-confirmed two-step flow, mirroring
 *              WalletActionModal.jsx — same server-side treatment (bcrypt
 *              re-verify, audit-logged).
 *
 *              ONE COMPONENT, TWO SCREENS. The Outstanding Dues queue and a
 *              single wallet's detail page both need this, and two copies of
 *              "how do you settle a debt" is how they start disagreeing about
 *              whether an overpayment is banked or clamped.
 *
 *              Settling and writing off are one form with one switch rather than
 *              two buttons: everything else about them is identical, and the one
 *              thing that differs — are we saying the money came in, or that we
 *              have given up on it — is exactly what the admin must state
 *              deliberately before confirming. They audit separately
 *              (wallet.due_payment / wallet.due_writeoff) so "recovered" and
 *              "written off" can never be read as one number.
 * @author      Kinetika Labs
 * @created     2026-10-06
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Icon from '../../components/Icon';
import PasswordInput from '../../components/PasswordInput';
import { toast } from '../../lib/toast';
import { settleUserDue, settleCompanyDue } from '../../api/wallets';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Record an offline payment against a due, or write it off. Password-confirmed
 * two-step flow, mirroring pages/wallet/WalletActionModal.jsx — same server-side
 * treatment (bcrypt re-verify, audit-logged).
 *
 * Settling and writing off are one form with one switch rather than two buttons:
 * everything else about them is identical, and the one thing that differs — are
 * we saying the money came in, or that we have given up on it — is exactly what
 * the admin must state deliberately before confirming.
 */
export default function SettleDueModal({ row, onClose }) {
  const qc = useQueryClient();
  const [step, setStep] = useState('details');
  const [amount, setAmount] = useState(String(row.dues));
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [writeOff, setWriteOff] = useState(false);
  const [password, setPassword] = useState('');
  const [fieldErr, setFieldErr] = useState({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const continueToConfirm = (e) => {
    e.preventDefault();
    setErr(''); setFieldErr({});
    const amt = Number(amount);
    if (!(amt > 0)) { setFieldErr({ amount: 'Enter an amount greater than zero.' }); return; }
    if (!reason.trim()) { setFieldErr({ reason: 'A reason is required.' }); return; }
    setStep('confirm');
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setFieldErr({});
    if (!password) { setFieldErr({ password: 'Your password is required.' }); return; }

    setBusy(true);
    try {
      const body = {
        amount: Number(amount),
        reference: reference.trim(),
        reason: reason.trim(),
        writeOff: writeOff ? 'true' : 'false',
        password,
      };
      const res = row.ownerType === 'business'
        ? await settleCompanyDue(row.ownerId, body)
        : await settleUserDue(row.ownerId, body);
      toast.ok(
        writeOff ? 'Due written off' : 'Payment recorded',
        `Applied ${inr(res?.data?.applied)} — ${inr(res?.data?.dues)} still outstanding`,
      );
      qc.invalidateQueries({ queryKey: ['credit-dues'] });
      onClose();
    } catch (e2) {
      const apiErr = e2?.response?.data?.error;
      if (apiErr?.code === 'BAD_CREDENTIALS') setFieldErr({ password: 'Incorrect password.' });
      else if (apiErr?.fields) {
        setFieldErr(apiErr.fields);
        setErr(apiErr?.message || '');
        if (Object.keys(apiErr.fields).some((k) => k !== 'password')) setStep('details');
      } else setErr(apiErr?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const amt = Number(amount) || 0;

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 460 }}>
        <div className="lk-modal-head">
          <h3>{step === 'confirm' ? 'Confirm with your password' : (writeOff ? 'Write off due' : 'Record payment')}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>

        {step === 'details' ? (
          <form className="det-body" onSubmit={continueToConfirm}>
            <p className="muted">
              <b>{row.ownerName}</b>
              <br />Outstanding: <b>{inr(row.dues)}</b>
            </p>

            <div className="field full">
              <label>Amount (₹)</label>
              <input
                type="number" min="0.01" max={row.dues} step="0.01"
                value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus
              />
              {fieldErr.amount ? <div className="field-error">{fieldErr.amount}</div> : null}
              <small className="field-hint">
                This lowers what is owed. It does <b>not</b> add spendable balance — use Credit on the
                wallet for that. More than the outstanding amount is clamped, never banked.
              </small>
            </div>

            <div className="field full">
              <label>Reference <span className="muted">(optional — e.g. UTR / cheque no.)</span></label>
              <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. UTR2026100612345" />
              {fieldErr.reference ? <div className="field-error">{fieldErr.reference}</div> : null}
            </div>

            <div className="field full">
              <label>Reason</label>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. NEFT received 06 Oct" />
              {fieldErr.reason ? <div className="field-error">{fieldErr.reason}</div> : null}
            </div>

            <label className="field full" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={writeOff} onChange={(e) => setWriteOff(e.target.checked)} />
              <span>Write this off instead — the money was never collected</span>
            </label>

            {err ? <div className="field-error">{err}</div> : null}

            <div className="form-actions">
              <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn primary">Continue</button>
            </div>
          </form>
        ) : (
          <form className="det-body" onSubmit={submit}>
            <p className="muted">
              {writeOff
                ? <>Writing off <b>{inr(amt)}</b> for <b>{row.ownerName}</b> — recorded as money we will not collect.</>
                : <>Recording <b>{inr(amt)}</b> received from <b>{row.ownerName}</b>.</>}
              {reference.trim() ? <> — <i>{reference.trim()}</i></> : null}
            </p>

            <div className="field full">
              <label>Confirm your password</label>
              <PasswordInput
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Your admin password" autoComplete="new-password"
                data-lpignore="true" data-1p-ignore autoFocus
              />
              {fieldErr.password ? <div className="field-error">{fieldErr.password}</div> : null}
            </div>

            {err ? <div className="field-error">{err}</div> : null}

            <div className="form-actions">
              <button
                type="button" className="btn ghost" disabled={busy}
                onClick={() => { setStep('details'); setPassword(''); setErr(''); setFieldErr({}); }}
              >
                Back
              </button>
              <button type="submit" className="btn primary" disabled={busy}>
                {busy ? 'Please wait…' : (writeOff ? 'Write off' : 'Record payment')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
